
import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  Transaction,
  FindAllRedemptionsQuery,
  FindAllRedemptionsQueryVariables
} from "../../../../schemaTypes";

// GraphQL query matching the resolver's output
export const FIND_ALL_REDEMPTIONS_QUERY = gql`
  query FindAllRedemptions(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllRedemptions(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        amount
        charge
        reference
        type
        status
        currency
        transferTypes
        bankPreference {
          id
        }
        offer {
          id
          title
          description
          bannerUrl
          category {
            id
            name
            imgSrc
          }
          offerType
          discountType
          discountValue
          partnerVoucher
          referralUrl
          amount
          status
          usageLimitPerUser
          totalUsageLimit
          startDate
          endDate
          community {
            id
            name
            alias
          }
          redemptions
          revenueGenerated
          clickThroughs
          featured
          createdBy {
            id
            role
            # admin {
            #   id
            #   shortId
            #   firstname
            #   lastname
            #   email
            #   phone
            #   merchantProfile {
            #     id
            #     businessName
            #     businessType
            #     regCountry
            #     businessRegNumber
            #     taxId
            #     address
            #     addressAlt
            #     city
            #     country
            #     postcode
            #     companyName
            #     username
            #     industry {
            #       id
            #       name
            #       createdBy {
            #         id
            #         role
            #       }
            #       createdAt
            #       updatedAt
            #     }
            #     companyLocation
            #     logoUrl
            #     bannerUrl
            #     description
            #     commissionRate
            #     createdAt
            #     updatedAt
            #   }
            #   createdAt
            #   updatedAt
            # }
          }
          createdAt
          updatedAt
        }
        njoyCode
        partnerVoucher
        user {
          id
          customer {
            firstname
            lastname
          }
          # createdAt
          # updatedAt
        }
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllRedemptions {
  redemptions: Transaction[];
  loading: boolean;
  loadMore: () => void;
  hasMore: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: FindAllRedemptionsQueryVariables;
  children: (data: WithFindAllRedemptions) => React.ReactElement | null;
}

export const FindAllRedemptions: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllRedemptionsQuery,
    FindAllRedemptionsQueryVariables
  >(FIND_ALL_REDEMPTIONS_QUERY, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const redemptions = data?.findAllRedemptions?.data || [];
  const page = data?.findAllRedemptions?.page || variables.page;
  const pageSize = data?.findAllRedemptions?.pageSize || variables.pageSize;
  const total = data?.findAllRedemptions?.total || 0;
  const totalPages = data?.findAllRedemptions?.totalPages || 0;
  const hasMore = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllRedemptions) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllRedemptions: {
            ...fetchMoreResult.findAllRedemptions,
            data: [
              ...(prev?.findAllRedemptions?.data || []),
              ...(fetchMoreResult.findAllRedemptions.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMore]);

  return children({
    redemptions: redemptions as any,
    loading,
    loadMore,
    hasMore,
    page,
    pageSize,
    total,
    totalPages,
  });
};